import { Card, Badge, ListGroup, Form, Button } from 'react-bootstrap';
import { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MyContext from '@/shared/contexts/AppContext';
import {
  RelatorioAtividadeDto,
  BuscarDefensorPorCPF,
} from '@/modules/reports/application/relatorios-atividades.service';
import { getTokenPayload } from '@/modules/reports/application/relatorios-atividades.utils';
import { GetDiretoriaDoUsuario } from '@/modules/structure/diretoria/diretoria.service';
import { BuscarRelatoriosParaAprovacao } from '@/modules/reports/application/relatorios-aprovacao.service';
import Links from '@/shared/constants/navegacao';
import { RELATORIO_CORES } from '../constants/relatorio-cores';

const STORAGE_KEY_OCULTAS_DEFENSOR = 'relatorios-atividades-notificacoes-ocultas';
const STORAGE_KEY_OCULTAS_DIRETORIA = 'relatorios-aprovacao-notificacoes-ocultas';
const STORAGE_KEY_EXPANDIDO_DEFENSOR = 'relatorios-atividades-notificacoes-expandido';
const STORAGE_KEY_EXPANDIDO_DIRETORIA = 'relatorios-aprovacao-notificacoes-expandido';
const LISTA_MAX_HEIGHT = 320;

const MESES: Record<number, string> = {
  1: 'janeiro', 2: 'fevereiro', 3: 'março', 4: 'abril', 5: 'maio', 6: 'junho',
  7: 'julho', 8: 'agosto', 9: 'setembro', 10: 'outubro', 11: 'novembro', 12: 'dezembro',
};

export type RelatoriosNotificacoesModo = 'defensor' | 'diretoria';

interface RelatoriosAtividadesNotificacoesProps {
  relatorios?: RelatorioAtividadeDto[];
  loading?: boolean;
  /** defensor: apenas alertas do próprio relatório (/relatorios/). diretoria: relatórios dos demais defensores da diretoria (/relatorios/aprovacao). */
  modo?: RelatoriosNotificacoesModo;
}

interface NotificacaoItem {
  id: string;
  tipo: 'envio' | 'feedback' | 'correcao' | 'validacao_pendente' | 'prazo';
  titulo: string;
  mensagem: string;
  data: Date | string;
  relatorioId?: number;
  relatorioPeriodo?: string;
  relatorioTipo?: string;
  lida?: boolean;
  linkAprovacao?: boolean;
}

/** Último dia do mês seguinte ao do relatório (prazo máximo de envio). */
function getUltimoDiaMesSeguinte(mes: number | string, ano: number): Date {
  const m = typeof mes === 'string' ? parseInt(mes, 10) : mes;
  return new Date(ano, m + 1, 0); // último dia do mês seguinte (JS: mês 0-indexed)
}

/** Primeiro dia do mês seguinte (a partir de quando o relatório pode ser enviado). */
function getPrimeiroDiaMesSeguinte(mes: number | string, ano: number): Date {
  const m = typeof mes === 'string' ? parseInt(mes, 10) : mes;
  if (m === 12) return new Date(ano + 1, 0, 1);
  return new Date(ano, m, 1); // JS: mês 0-indexed; mes 1 (Jan) -> (ano, 1, 1) = 1º Fev
}

/** Data inicial do uso dos relatórios: a partir deste mês/ano são exibidas notificações de prazo. */
const DATA_INICIAL_RELATORIOS = { mes: 1, ano: 2026 };

function diasEntre(hoje: Date, data: Date): number {
  const a = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const b = new Date(data.getFullYear(), data.getMonth(), data.getDate());
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
}

function loadIdsOcultasParaChave(chave: string): Set<string> {
  try {
    const raw = localStorage.getItem(chave);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function saveIdsOcultasParaChave(chave: string, ids: Set<string>) {
  try {
    localStorage.setItem(chave, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

export default function RelatoriosAtividadesNotificacoes({
  relatorios = [],
  loading = false,
  modo = 'defensor',
}: RelatoriosAtividadesNotificacoesProps) {
  const navigate = useNavigate();
  const context = useContext(MyContext);
  const storageKeyOcultas = useMemo(
    () => (modo === 'diretoria' ? STORAGE_KEY_OCULTAS_DIRETORIA : STORAGE_KEY_OCULTAS_DEFENSOR),
    [modo],
  );
  const storageKeyExpandido = useMemo(
    () => (modo === 'diretoria' ? STORAGE_KEY_EXPANDIDO_DIRETORIA : STORAGE_KEY_EXPANDIDO_DEFENSOR),
    [modo],
  );

  const [diretoriaId, setDiretoriaId] = useState<number | null>(null);
  const [pendentesAprovacao, setPendentesAprovacao] = useState<number>(0);
  const [loadingPendentes, setLoadingPendentes] = useState(modo === 'diretoria');
  const [relatoriosDiretoriaOutros, setRelatoriosDiretoriaOutros] = useState<RelatorioAtividadeDto[]>([]);
  const [idsOcultos, setIdsOcultos] = useState<Set<string>>(new Set());
  const [idsSelecionados, setIdsSelecionados] = useState<Set<string>>(new Set());
  const [expandido, setExpandido] = useState(true);

  useEffect(() => {
    setIdsOcultos(loadIdsOcultasParaChave(storageKeyOcultas));
    setIdsSelecionados(new Set());
  }, [storageKeyOcultas]);

  useEffect(() => {
    try {
      const v = localStorage.getItem(storageKeyExpandido);
      setExpandido(v !== 'false');
    } catch {
      setExpandido(true);
    }
  }, [storageKeyExpandido]);

  const ocultarNotificacao = useCallback(
    (id: string) => {
      setIdsOcultos((prev) => {
        const next = new Set(prev);
        next.add(id);
        saveIdsOcultasParaChave(storageKeyOcultas, next);
        return next;
      });
      setIdsSelecionados((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [storageKeyOcultas],
  );

  const ocultarEmLote = useCallback(() => {
    if (idsSelecionados.size === 0) return;
    setIdsOcultos((prev) => {
      const next = new Set(prev);
      idsSelecionados.forEach((id) => next.add(id));
      saveIdsOcultasParaChave(storageKeyOcultas, next);
      return next;
    });
    setIdsSelecionados(new Set());
  }, [idsSelecionados, storageKeyOcultas]);

  const toggleSelecao = useCallback((id: string) => {
    setIdsSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleExpandido = useCallback(() => {
    setExpandido((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKeyExpandido, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, [storageKeyExpandido]);

  /** Diretoria: carrega todos os relatórios da diretoria e remove o do próprio usuário (perfil defensor+diretor). */
  useEffect(() => {
    if (modo !== 'diretoria') {
      setDiretoriaId(null);
      setPendentesAprovacao(0);
      setRelatoriosDiretoriaOutros([]);
      setLoadingPendentes(false);
      return;
    }

    let cancelled = false;
    const carregar = async () => {
      setLoadingPendentes(true);
      try {
        const cpf = context?.state?.cpf ?? getTokenPayload()?.personCPF;
        const defensorLogado = cpf ? await BuscarDefensorPorCPF(cpf) : null;
        const diretoria = await GetDiretoriaDoUsuario();
        if (cancelled) return;
        if (!diretoria?.id) {
          setDiretoriaId(null);
          setRelatoriosDiretoriaOutros([]);
          setPendentesAprovacao(0);
          return;
        }
        setDiretoriaId(diretoria.id);
        const lista = await BuscarRelatoriosParaAprovacao(diretoria.id);
        if (cancelled) return;
        const filtrada =
          defensorLogado?.id != null
            ? lista.filter((r) => r.defensorId !== defensorLogado.id)
            : lista;
        setRelatoriosDiretoriaOutros(filtrada);
        setPendentesAprovacao(filtrada.filter((r) => r.status === 'enviado').length);
      } catch {
        if (!cancelled) {
          setDiretoriaId(null);
          setRelatoriosDiretoriaOutros([]);
          setPendentesAprovacao(0);
        }
      } finally {
        if (!cancelled) setLoadingPendentes(false);
      }
    };
    carregar();
    return () => {
      cancelled = true;
    };
  }, [modo, context?.state?.cpf]);

  // Função para formatar data
  const formatarData = (data: Date | string | undefined): string => {
    if (!data) return 'Data não disponível';
    
    try {
      const dataObj = typeof data === 'string' ? new Date(data) : data;
      if (isNaN(dataObj.getTime())) return 'Data inválida';
      
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(dataObj);
    } catch {
      return 'Data inválida';
    }
  };

  // Função para obter o label do tipo de relatório
  const getTipoLabel = (tipo?: string): string => {
    return tipo === 'civil' ? 'Cível' : tipo === 'criminal' ? 'Criminal' : 'N/A';
  };

  // Gerar notificações baseadas nos relatórios enviados
  const gerarNotificacoes = (): NotificacaoItem[] => {
    const notificacoes: NotificacaoItem[] = [];

    // Notificações de envio (relatórios enviados recentemente)
    relatorios
      .filter(r => r.status === 'enviado' && r.dataEnvio)
      .sort((a, b) => {
        const dataA = new Date(a.dataEnvio || 0).getTime();
        const dataB = new Date(b.dataEnvio || 0).getTime();
        return dataB - dataA; // Mais recentes primeiro
      })
      .slice(0, 5) // Limitar a 5 notificações mais recentes
      .forEach(relatorio => {
        notificacoes.push({
          id: `envio-${relatorio.id}`,
          tipo: 'envio',
          titulo: 'Relatório Enviado',
          mensagem: `Relatório ${getTipoLabel(relatorio.tipo)} de ${relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`} foi enviado com sucesso.`,
          data: relatorio.dataEnvio || relatorio.dataCriacao || new Date(),
          relatorioId: relatorio.id,
          relatorioPeriodo: relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`,
          relatorioTipo: relatorio.tipo,
          lida: false,
        });
      });

    // Notificações de prazo (relatórios em rascunho/pendente: prazo = último dia do mês seguinte)
    const hoje = new Date();
    const mesAtual = hoje.getMonth() + 1;
    const anoAtual = hoje.getFullYear();
    relatorios
      .filter(r => r.status === 'rascunho' || r.status === 'pendente')
      .forEach(relatorio => {
        const mes = typeof relatorio.mes === 'string' ? parseInt(relatorio.mes, 10) : relatorio.mes;
        const ano = relatorio.ano;
        const prazo = getUltimoDiaMesSeguinte(mes, ano);
        const primeiroEnvio = getPrimeiroDiaMesSeguinte(mes, ano);
        const diasRestantes = diasEntre(hoje, prazo);
        const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
        const prazoStr = prazo.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        let titulo: string;
        let mensagem: string;
        if (diasRestantes < 0) {
          titulo = 'Prazo encerrado';
          mensagem = `O prazo de envio do relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} era até ${prazoStr}. Envie o quanto antes.`;
        } else if (hoje < primeiroEnvio) {
          titulo = 'Prazo de envio';
          const primeiroStr = primeiroEnvio.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          mensagem = `Relatório ${getTipoLabel(relatorio.tipo)} de ${periodo}: pode ser enviado a partir de ${primeiroStr}. Prazo final: ${prazoStr}.`;
        } else {
          titulo = 'Prazo de envio';
          mensagem = diasRestantes === 0
            ? `Último dia! Envie o relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} até hoje (${prazoStr}).`
            : `Relatório ${getTipoLabel(relatorio.tipo)} de ${periodo}: enviar até ${prazoStr}. Faltam ${diasRestantes} dia(s).`;
        }
        notificacoes.push({
          id: `prazo-${relatorio.id}`,
          tipo: 'prazo',
          titulo,
          mensagem,
          data: hoje,
          relatorioId: relatorio.id,
          relatorioPeriodo: periodo,
          relatorioTipo: relatorio.tipo,
          lida: false,
        });
      });

    // Notificações de prazo para períodos sem nenhum relatório (desde a data inicial jan/2026 até o mês passado)
    const periodosComRelatorio = new Set(relatorios.map(r => {
      const m = typeof r.mes === 'string' ? parseInt(r.mes, 10) : r.mes;
      return `${m}-${r.ano}`;
    }));
    for (let y = DATA_INICIAL_RELATORIOS.ano; y <= anoAtual; y++) {
      const inicioMes = (y === DATA_INICIAL_RELATORIOS.ano) ? DATA_INICIAL_RELATORIOS.mes : 1;
      const fimMes = (y === anoAtual) ? mesAtual - 1 : 12;
      for (let m = inicioMes; m <= fimMes; m++) {
        const key = `${m}-${y}`;
        if (periodosComRelatorio.has(key)) continue;
        const prazo = getUltimoDiaMesSeguinte(m, y);
        const primeiroEnvio = getPrimeiroDiaMesSeguinte(m, y);
        const diasRestantes = diasEntre(hoje, prazo);
        const periodoStr = `${MESES[m]}/${y}`;
        const prazoStr = prazo.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        let titulo: string;
        let mensagem: string;
        if (diasRestantes < 0) {
          titulo = 'Prazo encerrado';
          mensagem = `O prazo de envio do relatório de ${periodoStr} era até ${prazoStr}. Você ainda não enviou. Envie o quanto antes.`;
        } else if (hoje < primeiroEnvio) {
          titulo = 'Prazo de envio';
          const primeiroStr = primeiroEnvio.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          mensagem = `Relatório de ${periodoStr}: pode ser enviado a partir de ${primeiroStr}. Prazo final: ${prazoStr}.`;
        } else {
          titulo = 'Prazo de envio';
          mensagem = diasRestantes === 0
            ? `Último dia! Envie o relatório de ${periodoStr} até hoje (${prazoStr}).`
            : `Relatório de ${periodoStr}: enviar até ${prazoStr}. Faltam ${diasRestantes} dia(s).`;
        }
        notificacoes.push({
          id: `prazo-mes-${m}-${y}`,
          tipo: 'prazo',
          titulo,
          mensagem,
          data: hoje,
          relatorioPeriodo: periodoStr,
          lida: false,
        });
      }
    }

    // Notificações de feedback: aprovado ou reprovado (tramitação)
    relatorios
      .filter(r => (r.status === 'aprovado' && r.dataAprovacao) || (r.status === 'reprovado' && r.dataReprovacao))
      .sort((a, b) => {
        const dataA = new Date((a.dataAprovacao || a.dataReprovacao || 0) as string).getTime();
        const dataB = new Date((b.dataAprovacao || b.dataReprovacao || 0) as string).getTime();
        return dataB - dataA;
      })
      .slice(0, 5)
      .forEach(relatorio => {
        const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
        if (relatorio.status === 'aprovado') {
          const avalCorregedoria = relatorio.corregedoriaAprovadoEm
            ? ` Aval final da Corregedoria registrado.`
            : ' Aguardando aval da Corregedoria.';
          notificacoes.push({
            id: `feedback-aprovado-${relatorio.id}`,
            tipo: 'feedback',
            titulo: 'Relatório Aprovado',
            mensagem: `Relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} foi aprovado pelo diretor.${avalCorregedoria}`,
            data: relatorio.dataAprovacao || relatorio.dataCriacao || new Date(),
            relatorioId: relatorio.id,
            relatorioPeriodo: periodo,
            relatorioTipo: relatorio.tipo,
            lida: false,
          });
        } else {
          notificacoes.push({
            id: `feedback-reprovado-${relatorio.id}`,
            tipo: 'feedback',
            titulo: 'Relatório Reprovado',
            mensagem: `Relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} foi reprovado.${relatorio.justificativaReprovacao ? ` Justificativa: ${relatorio.justificativaReprovacao}` : ''}`,
            data: relatorio.dataReprovacao || relatorio.dataCriacao || new Date(),
            relatorioId: relatorio.id,
            relatorioPeriodo: periodo,
            relatorioTipo: relatorio.tipo,
            lida: false,
          });
        }
      });

    // Ordenar por data (mais recente primeiro)
    notificacoes.sort((a, b) => {
      const dataA = typeof a.data === 'string' ? new Date(a.data).getTime() : (a.data as Date).getTime();
      const dataB = typeof b.data === 'string' ? new Date(b.data).getTime() : (b.data as Date).getTime();
      return dataB - dataA;
    });
    return notificacoes.slice(0, 10); // Limitar total exibido
  };

  /** Notificações para o diretor: relatórios de outros defensores da diretoria (exceto o próprio). */
  const gerarNotificacoesDiretoria = (lista: RelatorioAtividadeDto[]): NotificacaoItem[] => {
    const notificacoes: NotificacaoItem[] = [];
    const hoje = new Date();

    lista
      .filter((r) => r.status === 'enviado' && r.dataEnvio)
      .sort((a, b) => {
        const dataA = new Date(a.dataEnvio || 0).getTime();
        const dataB = new Date(b.dataEnvio || 0).getTime();
        return dataB - dataA;
      })
      .slice(0, 6)
      .forEach((relatorio) => {
        const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
        const def = relatorio.defensorNome?.trim() || 'Defensor';
        notificacoes.push({
          id: `diretoria-envio-${relatorio.id}`,
          tipo: 'envio',
          titulo: 'Relatório aguardando aprovação',
          mensagem: `${def} enviou o relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} para análise da diretoria.`,
          data: relatorio.dataEnvio || relatorio.dataCriacao || new Date(),
          relatorioId: relatorio.id,
          relatorioPeriodo: relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`,
          relatorioTipo: relatorio.tipo,
          lida: false,
        });
      });

    lista
      .filter((r) => r.status === 'rascunho' || r.status === 'pendente')
      .forEach((relatorio) => {
        const mes = typeof relatorio.mes === 'string' ? parseInt(relatorio.mes, 10) : relatorio.mes;
        const ano = relatorio.ano;
        const prazo = getUltimoDiaMesSeguinte(mes, ano);
        const primeiroEnvio = getPrimeiroDiaMesSeguinte(mes, ano);
        const diasRestantes = diasEntre(hoje, prazo);
        const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
        const prazoStr = prazo.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const def = relatorio.defensorNome?.trim() || 'Defensor';
        let titulo: string;
        let mensagem: string;
        if (diasRestantes < 0) {
          titulo = 'Prazo encerrado (defensor)';
          mensagem = `Prazo de envio do relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} (${def}) era até ${prazoStr}.`;
        } else if (hoje < primeiroEnvio) {
          titulo = 'Prazo de envio (defensor)';
          const primeiroStr = primeiroEnvio.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          mensagem = `Relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} — ${def}: envio a partir de ${primeiroStr}. Prazo final: ${prazoStr}.`;
        } else {
          titulo = 'Prazo de envio (defensor)';
          mensagem =
            diasRestantes === 0
              ? `Último dia para ${def} enviar o relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} (${prazoStr}).`
              : `Relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} — ${def}: enviar até ${prazoStr}. Faltam ${diasRestantes} dia(s).`;
        }
        notificacoes.push({
          id: `diretoria-prazo-${relatorio.id}`,
          tipo: 'prazo',
          titulo,
          mensagem,
          data: hoje,
          relatorioId: relatorio.id,
          relatorioPeriodo: periodo,
          relatorioTipo: relatorio.tipo,
          lida: false,
        });
      });

    lista
      .filter(
        (r) => (r.status === 'aprovado' && r.dataAprovacao) || (r.status === 'reprovado' && r.dataReprovacao),
      )
      .sort((a, b) => {
        const dataA = new Date((a.dataAprovacao || a.dataReprovacao || 0) as string).getTime();
        const dataB = new Date((b.dataAprovacao || b.dataReprovacao || 0) as string).getTime();
        return dataB - dataA;
      })
      .slice(0, 5)
      .forEach((relatorio) => {
        const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
        const def = relatorio.defensorNome?.trim() || 'Defensor';
        if (relatorio.status === 'aprovado') {
          const avalCorregedoria = relatorio.corregedoriaAprovadoEm
            ? ' Aval final da Corregedoria registrado.'
            : ' Aguardando aval da Corregedoria.';
          notificacoes.push({
            id: `diretoria-feedback-aprovado-${relatorio.id}`,
            tipo: 'feedback',
            titulo: 'Relatório aprovado pela diretoria',
            mensagem: `Relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} (${def}) foi aprovado pela diretoria.${avalCorregedoria}`,
            data: relatorio.dataAprovacao || relatorio.dataCriacao || new Date(),
            relatorioId: relatorio.id,
            relatorioPeriodo: periodo,
            relatorioTipo: relatorio.tipo,
            lida: false,
          });
        } else {
          const origem = relatorio.dataAprovacao ? 'Corregedoria' : 'Diretoria';
          notificacoes.push({
            id: `diretoria-feedback-reprovado-${relatorio.id}`,
            tipo: 'feedback',
            titulo: 'Relatório reprovado',
            mensagem: `Relatório ${getTipoLabel(relatorio.tipo)} de ${periodo} (${def}) foi reprovado (${origem}).${relatorio.justificativaReprovacao ? ` Justificativa: ${relatorio.justificativaReprovacao}` : ''}`,
            data: relatorio.dataReprovacao || relatorio.dataCriacao || new Date(),
            relatorioId: relatorio.id,
            relatorioPeriodo: periodo,
            relatorioTipo: relatorio.tipo,
            lida: false,
          });
        }
      });

    notificacoes.sort((a, b) => {
      const dataA = typeof a.data === 'string' ? new Date(a.data).getTime() : (a.data as Date).getTime();
      const dataB = typeof b.data === 'string' ? new Date(b.data).getTime() : (b.data as Date).getTime();
      return dataB - dataA;
    });
    return notificacoes.slice(0, 10);
  };

  // Notificação de validação pendente (somente na tela de aprovação / modo diretoria)
  const notificacaoValidacaoPendente: NotificacaoItem | null =
    modo === 'diretoria' &&
    !loadingPendentes &&
    diretoriaId != null &&
    pendentesAprovacao > 0
      ? {
          id: 'validacao-pendente',
          tipo: 'validacao_pendente',
          titulo: 'Validação pendente',
          mensagem: `Há ${pendentesAprovacao} relatório(s) aguardando sua aprovação como diretor(a) da diretoria regional.`,
          data: new Date(),
          lida: false,
          linkAprovacao: true,
        }
      : null;

  const notificacoesBase =
    modo === 'diretoria' ? gerarNotificacoesDiretoria(relatoriosDiretoriaOutros) : gerarNotificacoes();
  const notificacoesBrutas =
    notificacaoValidacaoPendente != null
      ? [notificacaoValidacaoPendente, ...notificacoesBase]
      : notificacoesBase;
  const notificacoes = notificacoesBrutas.filter(n => !idsOcultos.has(n.id));
  const notificacoesNaoLidas = notificacoes.filter(n => !n.lida).length;

  const toggleSelecionarTodas = useCallback(() => {
    setIdsSelecionados(prev => {
      if (prev.size === notificacoes.length) return new Set();
      return new Set(notificacoes.map(n => n.id));
    });
  }, [notificacoes]);

  const cardStyle = {
    border: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px',
    boxShadow: RELATORIO_CORES.SOMBRA_CARD,
  };
  const headerStyle = {
    backgroundColor: RELATORIO_CORES.CINZA_CLARO,
    color: RELATORIO_CORES.PRIMARIA,
    borderBottom: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px 8px 0 0',
  };

  const carregandoUi =
    (modo === 'defensor' && loading) || (modo === 'diretoria' && loadingPendentes);

  if (carregandoUi) {
    return (
      <Card className="mb-4" style={cardStyle}>
        <Card.Header style={headerStyle} className="d-flex align-items-center">
          <i className="bi bi-bell me-2" style={{ color: RELATORIO_CORES.PRIMARIA }}></i>
          <h5 className="mb-0 fw-bold" style={{ color: RELATORIO_CORES.PRIMARIA }}>Notificações</h5>
        </Card.Header>
        <Card.Body style={{ backgroundColor: RELATORIO_CORES.BRANCO }}>
          Carregando notificações...
        </Card.Body>
      </Card>
    );
  }

  if (notificacoes.length === 0) {
    return (
      <Card className="mb-4" style={cardStyle}>
        <Card.Header style={headerStyle} className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold">
            <i className="bi bi-bell me-2" style={{ color: RELATORIO_CORES.PRIMARIA }}></i>
            <span style={{ color: RELATORIO_CORES.PRIMARIA }}>Notificações</span>
          </h5>
          <Badge style={{ backgroundColor: RELATORIO_CORES.CINZA_MEDIO, color: RELATORIO_CORES.BRANCO }}>0</Badge>
        </Card.Header>
        <Card.Body style={{ backgroundColor: RELATORIO_CORES.BRANCO }}>
          <p className="mb-0 text-center text-muted">
            <i className="bi bi-inbox me-2"></i>
            Nenhuma notificação no momento.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-4" style={cardStyle}>
      <Card.Header
        style={{ ...headerStyle, cursor: 'pointer' }}
        className="d-flex justify-content-between align-items-center"
        onClick={toggleExpandido}
        role="button"
        aria-expanded={expandido}
        aria-label={expandido ? 'Ocultar notificações' : 'Exibir notificações'}
      >
        <h5 className="mb-0 fw-bold d-flex align-items-center">
          <i
            className={`bi me-2 ${expandido ? 'bi-chevron-down' : 'bi-chevron-right'}`}
            style={{ color: RELATORIO_CORES.PRIMARIA, fontSize: '1rem' }}
          />
          <i className="bi bi-bell me-2" style={{ color: RELATORIO_CORES.PRIMARIA }}></i>
          <span style={{ color: RELATORIO_CORES.PRIMARIA }}>Notificações</span>
        </h5>
        {notificacoesNaoLidas > 0 && (
          <Badge pill style={{ backgroundColor: RELATORIO_CORES.SECUNDARIA, color: '#1a1a1a' }}>
            {notificacoesNaoLidas}
          </Badge>
        )}
      </Card.Header>
      {expandido && (
        <Card.Body className="p-0" style={{ backgroundColor: RELATORIO_CORES.BRANCO }}>
          {notificacoes.length > 0 && (
            <div
              className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
              style={{ backgroundColor: RELATORIO_CORES.CINZA_CLARO, borderColor: RELATORIO_CORES.BORDA_CARD }}
            >
              <Form.Check
                type="checkbox"
                id="notif-selecionar-todas"
                label={<span className="small">Selecionar todas</span>}
                checked={idsSelecionados.size === notificacoes.length && notificacoes.length > 0}
                onChange={toggleSelecionarTodas}
              />
              {idsSelecionados.size > 0 ? (
                <Button variant="outline-danger" size="sm" onClick={ocultarEmLote}>
                  Excluir selecionadas ({idsSelecionados.size})
                </Button>
              ) : (
                <span className="small text-muted">Marque as que deseja excluir em lote</span>
              )}
            </div>
          )}
          <ListGroup
            variant="flush"
            style={{
              maxHeight: LISTA_MAX_HEIGHT,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {notificacoes.map((notificacao) => {
              const getIcone = () => {
                switch (notificacao.tipo) {
                  case 'envio':
                    return <i className="bi bi-check-circle-fill text-success"></i>;
                  case 'feedback':
                    return <i className="bi bi-chat-dots-fill text-info"></i>;
                  case 'correcao':
                    return <i className="bi bi-exclamation-triangle-fill text-warning"></i>;
                  case 'prazo':
                    return <i className="bi bi-calendar-event-fill text-warning"></i>;
                  case 'validacao_pendente':
                    return <i className="bi bi-clipboard-check-fill" style={{ color: RELATORIO_CORES.PRIMARIA }}></i>;
                  default:
                    return <i className="bi bi-bell-fill text-secondary"></i>;
                }
              };

              const getBadgeVariant = (): string => {
                switch (notificacao.tipo) {
                  case 'envio':
                    return 'success';
                  case 'feedback':
                    return 'info';
                  case 'correcao':
                  case 'prazo':
                    return 'warning';
                  case 'validacao_pendente':
                    return 'primary';
                  default:
                    return 'secondary';
                }
              };

              const isClickable = notificacao.tipo === 'validacao_pendente' && (notificacao as NotificacaoItem).linkAprovacao;

              return (
                <ListGroup.Item
                  key={notificacao.id}
                  className={!notificacao.lida ? 'bg-light' : ''}
                  style={{
                    borderLeft: !notificacao.lida ? `3px solid ${RELATORIO_CORES.PRIMARIA}` : undefined,
                    cursor: isClickable ? 'pointer' : undefined,
                  }}
                  onClick={isClickable ? () => navigate(Links.Relatorios_Aprovacao) : undefined}
                  action={isClickable}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="flex-grow-1 min-width-0">
                      <div className="d-flex align-items-center mb-1 flex-wrap">
                        {getIcone()}
                        <strong className="ms-2">{notificacao.titulo}</strong>
                        <Badge
                          className="ms-2"
                          style={{
                            fontSize: '0.7rem',
                            ...(notificacao.tipo === 'validacao_pendente'
                              ? { backgroundColor: RELATORIO_CORES.PRIMARIA, color: RELATORIO_CORES.TERCIARIA }
                              : {}),
                          }}
                          bg={notificacao.tipo === 'validacao_pendente' ? undefined : getBadgeVariant()}
                        >
                          {notificacao.tipo === 'validacao_pendente' ? 'Aprovação' : getTipoLabel(notificacao.relatorioTipo)}
                        </Badge>
                        {!notificacao.lida && (
                          <Badge pill className="ms-2" style={{ fontSize: '0.65rem', backgroundColor: RELATORIO_CORES.PRIMARIA, color: RELATORIO_CORES.TERCIARIA }}>
                            Nova
                          </Badge>
                        )}
                      </div>
                      <p className="mb-1 text-muted small">{notificacao.mensagem}</p>
                      <small className="text-muted">
                        <i className="bi bi-calendar3 me-1"></i>
                        {formatarData(notificacao.data)}
                        {notificacao.relatorioPeriodo && (
                          <span className="ms-2">
                            <i className="bi bi-clock-history me-1"></i>
                            Período: {notificacao.relatorioPeriodo}
                          </span>
                        )}
                      </small>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        id={`notif-sel-${notificacao.id}`}
                        checked={idsSelecionados.has(notificacao.id)}
                        onChange={() => toggleSelecao(notificacao.id)}
                        aria-label="Selecionar para excluir em lote"
                      />
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-danger text-decoration-none"
                        style={{ fontSize: '1.25rem', lineHeight: 1, minWidth: '28px' }}
                        title="Excluir notificação"
                        aria-label="Excluir notificação"
                        onClick={() => ocultarNotificacao(notificacao.id)}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Card.Body>
      )}
      {expandido && notificacoes.length > 5 && (
        <Card.Footer className="text-center text-muted" style={{ backgroundColor: RELATORIO_CORES.CINZA_CLARO, borderTop: `1px solid ${RELATORIO_CORES.BORDA_CARD}` }}>
          <small>
            Mostrando {notificacoes.length} notificação(ões). Clique no cabeçalho para ocultar a lista.
          </small>
        </Card.Footer>
      )}
    </Card>
  );
}
